import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { formatNumber } from '../../utils/functions';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import useUserStore from '../../../../common/src/stores/useUserStore';

interface WeeklyData {
  week: number;
  comprometido: number;
  enviados: number;
}

interface CommercialPlanChartProps {
  data: WeeklyData[];
  /** Título del eje y abreviatura de la unidad del programa. */
  unitLabel?: string;
  unitShort?: string;
  decimals?: 0 | 1 | 2;
}

const CommercialPlanChart: React.FC<CommercialPlanChartProps> = ({
  data,
  unitLabel,
  unitShort = 'CEQ',
  decimals = 0,
}) => {
  const { t } = useTranslation('commitments');
  const { darkMode } = useUserStore();

  // Prepare chart data
  const categories = data.map((item) => `${item.week}`);
  const comprometidoData = data.map((item) => item.comprometido);
  const enviadosData = data.map((item) => item.enviados);

  const series = [
    {
      name: t('Comprometido'),
      data: comprometidoData,
      color: '#3b82f6', // Blue
    },
    {
      name: t('Despachado'),
      data: enviadosData,
      color: '#10b981', // Green
    },
  ];

  const chartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 400,
      foreColor: darkMode ? '#A1A1AA' : '#71717A',
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60%',
        endingShape: 'rounded',
        borderRadius: 4,
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: false,
      offsetY: -20,
      style: {
        fontSize: '12px',
        colors: ['#304758'],
      },
      formatter: function (val: number) {
        return val ? formatNumber(val, { decimals }) : '';
      },
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories: categories,
      title: {
        text: t('Semanas'),
        style: {
          fontSize: '14px',
          fontWeight: 'bold',
        },
      },
      labels: {
        rotate: -45,
        style: {
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      title: {
        text: unitLabel ?? t('Cajas Equivalentes'),
        style: {
          fontSize: '14px',
          fontWeight: 'bold',
        },
      },
      labels: {
        formatter: function (value) {
          return `${formatNumber(value, { decimals })}`;
        },
      },
    },
    fill: {
      opacity: 0.8,
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: function (
          val: number,
          opts: {
            seriesIndex: number;
            dataPointIndex: number;
            [key: string]: any;
          }
        ) {
          const { seriesIndex, dataPointIndex } = opts || ({} as any);
          const dataPoint = data[dataPointIndex];
          if (seriesIndex === 0) {
            return `${formatNumber(val, { decimals })} ${unitShort}`;
          } else {
            const comprometido = dataPoint?.comprometido || 0;
            const compliance = comprometido
              ? ((val / comprometido) * 100).toFixed(1)
              : '0.0';
            return `${formatNumber(val, { decimals })} ${unitShort} (${compliance}% ${t('cumplimiento')})`;
          }
        },
      },
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      floating: false,
      offsetY: 10,
      markers: {
        width: 12,
        height: 12,
        radius: 6,
      },
    },
    grid: {
      borderColor: '#e7e7e7',
      strokeDashArray: 3,
    },
    colors: ['#3b82f6', '#10b981'],
  };

  return (
    <div className='w-full'>
      <ReactApexChart
        options={chartOptions}
        series={series}
        type='bar'
        height={400}
      />
    </div>
  );
};

export default CommercialPlanChart;
